import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { FilterBar } from "@/components/filter-bar";
import { SearchMultiSelect } from "@/components/search-multi-select";
import { TimeRangeFilter } from "@/components/time-range-filter";
import { ToolIcon } from "@/components/tool-icon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminMcpUsage, useAdminMcpUsers, useConnectorOverview } from "@/lib/api";
import { useUrlTableSort } from "@/lib/table-sort";
import {
  DEFAULT_TIME_RANGE,
  sameTimeRange,
  timeRangeQuery,
  useTimeRange,
  withTimeRangeParams,
} from "@/lib/time-range";
import { isRetiredTool, toolLabel, useToolTitles } from "@/lib/tool-titles";

import { personOptionName, personOptions, toolNames } from "../utils";
import { CALL_SORT_COLUMNS, CALL_SORT_INITIAL, CallLog } from "./call-log";

/** How a call ended, as the outcome filter offers it. */
const OUTCOMES = ["all", "ok", "failed"] as const;
type Outcome = (typeof OUTCOMES)[number];

/** The page's search parameters for its filters, besides the time range and the sort. */
const PARAMS = { tool: "tool", person: "person", outcome: "outcome" } as const;

/** The days whose tools and people the filters offer, besides the catalogue and everyone known: a quarter. */
const OPTIONS_DAYS = 90;

function parseOutcome(value: string | null): Outcome {
  return value === "ok" || value === "failed" ? value : "all";
}

/**
 * Statistieken's Oproepen: the call log, with its filters in the tinted bar it
 * opens with: the time range (a period, or a custom range from one moment to
 * another), the tools and the people (each a list you can type in), and the
 * outcome. Every filter and the sort live in the URL (`?tool=…&person=…`), so
 * a link from Overzicht's tables opens the log filtered on a tool or a person.
 * Sorted and paged on the server.
 */
export function CallsPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [range, setRange] = useTimeRange();
  const tools = params.getAll(PARAMS.tool);
  const people = params.getAll(PARAMS.person);
  const outcome = parseOutcome(params.get(PARAMS.outcome));
  const sort = useUrlTableSort({ columns: CALL_SORT_COLUMNS, initial: CALL_SORT_INITIAL });

  // The choices of the filters: every tool and every person of the last quarter, with
  // the tools of the catalogue and everyone the connector knows.
  const titles = useToolTitles();
  const overviewQuery = useConnectorOverview();
  const usersQuery = useAdminMcpUsers();
  const usageQuery = useAdminMcpUsage(OPTIONS_DAYS);
  const usage = usageQuery.data;
  const knownTools = toolNames(
    overviewQuery.data?.tools ?? [],
    usage?.per_tool.map((row) => row.tool) ?? [],
  );
  const toolOptions = knownTools.map((name) => ({
    value: name,
    label: toolLabel(titles, name),
    detail: name,
    icon: <ToolIcon name={name} className="text-primary size-4" />,
    note: isRetiredTool(titles, name) ? t("tools.retired.label") : undefined,
  }));
  const peopleOptions = personOptions(usersQuery.data ?? [], usage?.per_user ?? []).map(
    (person) => ({
      value: person.oid,
      label: personOptionName(person),
      detail: person.name ? (person.email ?? undefined) : undefined,
    }),
  );

  const setList = (name: string, values: readonly string[]) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(name);
        values.forEach((value) => next.append(name, value));
        return next;
      },
      { replace: true },
    );
  const setOutcome = (value: Outcome) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value === "all") {
          next.delete(PARAMS.outcome);
        } else {
          next.set(PARAMS.outcome, value);
        }
        return next;
      },
      { replace: true },
    );
  const filtered =
    tools.length > 0 ||
    people.length > 0 ||
    outcome !== "all" ||
    !sameTimeRange(range, DEFAULT_TIME_RANGE);
  const reset = () =>
    setParams(
      (current) => {
        const next = withTimeRangeParams(current, DEFAULT_TIME_RANGE);
        Object.values(PARAMS).forEach((name) => next.delete(name));
        return next;
      },
      { replace: true },
    );

  return (
    <div className="space-y-6">
      <FilterBar
        tinted
        columns={["wideRow", "medium", "medium", "medium"]}
        filtered={filtered}
        onReset={reset}
      >
        <TimeRangeFilter value={range} onChange={setRange} />
        <SearchMultiSelect
          className="w-full"
          options={toolOptions}
          value={tools}
          onChange={(value) => setList(PARAMS.tool, value)}
          monoDetail
          name={t("analytics.tool")}
          allLabel={t("analytics.allTools")}
          countLabel={(count) => t("filters.toolCount", { count })}
          searchLabel={t("analytics.searchTool")}
        />
        <SearchMultiSelect
          className="w-full"
          options={peopleOptions}
          value={people}
          onChange={(value) => setList(PARAMS.person, value)}
          name={t("analytics.people")}
          allLabel={t("analytics.everyone")}
          countLabel={(count) => t("filters.personCount", { count })}
          searchLabel={t("analytics.searchPerson")}
        />
        <Select value={outcome} onValueChange={(value) => setOutcome(parseOutcome(value))}>
          <SelectTrigger size="sm" aria-label={t("analytics.outcome.label")} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OUTCOMES.map((option) => (
              <SelectItem key={option} value={option}>
                {t(`analytics.outcome.${option}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterBar>
      <CallLog
        query={{
          ...timeRangeQuery(range),
          tools,
          userOids: people,
          outcome: outcome === "all" ? undefined : outcome,
        }}
        sort={sort}
        label={t("analytics.tabs.calls")}
        onReset={filtered ? reset : undefined}
        seen={{
          tools: knownTools,
          errorKinds: usage?.per_error_type.map((row) => row.error_type) ?? [],
        }}
      />
    </div>
  );
}
